// Global feedback wiring: one set of document listeners gives every press its haptic and tone,
// every empty-space click its sparkle, every key its tap and every hover its note. Nothing has to
// be wired up where elements are rendered. Call initFeedback() once at startup.
//
// Adapted from the ModelLoop design handoff (design-handoff/feedback.ts).

import { haptics } from "./haptics";
import { sounds } from "./sounds";
import { isEmptySpaceClick, sparkleAt } from "./sparkle";
import { initStretchy } from "./stretchy";
import { getZoom, getSparkle } from "../prefs";

// Buttons that play a stronger or special response. Everything else gets "selection" (a tick).
const WARNING_BUTTONS = ".confirm-destructive";
const STOP_BUTTONS = ".is-stopping";
const SILENT_BUTTONS = ".plays-own-sound"; // buttons whose handler plays a named sound itself
const MEDIUM_BUTTONS = ".btn-save";

// Clickable things that are not buttons
const MEDIUM_TARGETS = ".theme-card";
const LIGHT_TARGETS = ".list-item, .suggestion-chip, .search-item";
const SELECTION_TARGETS = ".nav-item, .menu-item, .wordmark";
const OVERLAYS = ["modal-overlay", "search-dismiss"];

// Text fields that tap as you type, and things that chime on hover (mouse only)
const TYPED_FIELD =
  "textarea, input:not([type]), input[type='text'], input[type='search'], input[type='email'], input[type='password']";
const HOVERABLE =
  ".icon-btn, .btn-primary, .btn-secondary, .suggestion-chip, .nav-item, .menu-item, .theme-card, .dock__app, .termwin__btn";

function feedback(e: MouseEvent) {
  const target = e.target as HTMLElement;

  const btn = target.closest("button:not(:disabled)") as HTMLElement | null;
  if (btn) {
    if (btn.matches(WARNING_BUTTONS)) haptics.trigger("warning");
    else if (btn.matches(STOP_BUTTONS)) haptics.trigger("rigid");
    else if (btn.matches(SILENT_BUTTONS)) haptics.trigger("selection", { silent: true });
    else if (btn.matches(MEDIUM_BUTTONS)) haptics.trigger("medium");
    else haptics.trigger("selection");
    return;
  }

  if (target.closest(MEDIUM_TARGETS)) return haptics.trigger("medium");
  if (target.closest(LIGHT_TARGETS)) return haptics.trigger("light");
  if (target.closest(SELECTION_TARGETS)) return haptics.trigger("selection");
  // Links that are styled as buttons answer like buttons
  if (target.closest("a.btn-primary, a.btn-secondary, a.icon-btn")) return haptics.trigger("selection");
  if (OVERLAYS.some((c) => target.classList.contains(c))) return haptics.trigger("light");

  if (getSparkle() && isEmptySpaceClick(e)) sparkleAt(e.clientX, e.clientY, { scale: getZoom() });
}

export function initFeedback() {
  initStretchy();

  // Feedback runs on the press itself, in the capture phase. On "click" it would wait for the
  // whole screen to re-render first, which a heavy screen turns into an audible lag.
  document.addEventListener(
    "pointerdown",
    (e) => {
      if (e.isPrimary && e.button === 0) feedback(e);
    },
    { capture: true },
  );

  // Keyboard presses (Enter or Space on a button) only reach "click", and carry no pointer
  // detail, so nothing pressed with a pointer gets its feedback twice
  document.addEventListener("click", (e) => {
    if (e.detail === 0) feedback(e);
  });

  // Typing: characters and the keys that change text tap; arrows, Shift, Tab stay quiet
  document.addEventListener("keydown", (e) => {
    const target = e.target as HTMLElement;
    if (!target.matches?.(TYPED_FIELD) || e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key.length === 1 || e.key === "Backspace" || e.key === "Delete") sounds.key();
  });

  // Hover: a faint note on coming onto something pressable
  let hovered: Element | null = null;
  document.addEventListener("pointerover", (e) => {
    if (e.pointerType !== "mouse") return;
    const el = (e.target as Element | null)?.closest?.(HOVERABLE) ?? null;
    if (el === hovered) return; // moving between parts of one button is still the same button
    hovered = el;
    if (el && !el.matches(":disabled")) sounds.hover();
  });

  // Sliders: a tick on each step
  let lastRangeValue = NaN;
  document.addEventListener("input", (e) => {
    const target = e.target;
    if (target instanceof HTMLInputElement && target.type === "range") {
      const value = parseFloat(target.value);
      if (value !== lastRangeValue) {
        lastRangeValue = value;
        haptics.trigger("selection");
      }
    }
  });
}
